import React from 'react';
import { Tag, Check, X } from 'lucide-react';

/**
 * Promo code / referral code input + applied-state display.
 *
 * Props:
 *  - promoCode (string)               - current input value
 *  - setPromoCode (fn)                - updates input value
 *  - promoSuccess (bool)              - true when a code is applied
 *  - promoError (string)              - error message to display under input
 *  - setPromoError (fn)               - clears error on input change
 *  - discount (number 0..1)           - currently-applied discount fraction
 *  - isReferral (bool)                - true if applied code is a referral
 *  - referrerInfo ({ name, email })   - referrer details (if isReferral)
 *  - onApply (fn)                     - called when user clicks Apply
 *  - onRemove (fn)                    - called when user clicks the X
 */
export const CheckoutPromoSection = ({
  promoCode,
  setPromoCode,
  promoSuccess,
  promoError,
  setPromoError,
  discount,
  isReferral,
  referrerInfo,
  onApply,
  onRemove,
}) => {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm" data-testid="promo-section">
      <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
        <Tag size={20} /> Promo or Referral Code
      </h2>

      {promoSuccess ? (
        <div className={`flex items-center justify-between rounded-xl p-4 border ${isReferral ? 'bg-emerald-50 border-emerald-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-2">
            <Check className={isReferral ? 'text-emerald-600' : 'text-green-600'} size={20} />
            <div>
              <span className={`font-bold ${isReferral ? 'text-emerald-800' : 'text-green-800'}`}>{promoCode.toUpperCase()}</span>
              <span className={isReferral ? 'text-emerald-600' : 'text-green-600'}> - {(discount * 100).toFixed(0)}% off</span>
              {isReferral && referrerInfo && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">Referred by {referrerInfo.name}</p>
              )}
            </div>
          </div>
          <button onClick={onRemove} className="text-gray-500 dark:text-neutral-500 hover:text-gray-700" data-testid="remove-promo-btn">
            <X size={20} />
          </button>
        </div>
      ) : (
        <div className="flex gap-3 w-full">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
            placeholder="Enter code here"
            className="flex-1 min-w-0 px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black"
            data-testid="promo-input"
          />
          <button
            onClick={onApply}
            className="shrink-0 whitespace-nowrap px-6 py-3 bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
            data-testid="apply-promo-btn"
          >
            Apply
          </button>
        </div>
      )}
      {promoError && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400" data-testid="promo-error">{promoError}</p>
      )}
    </div>
  );
};
